import React, { useEffect, useState } from 'react';

/**
 * In-app message ("toast") from the kid-first design: a dark rounded bar near the bottom of the
 * screen with an optional action button (step 6 uses it for "Undo"). Replaces native alert()
 * pop-ups, which look like "localhost:3100 says…" and block the app.
 *
 * Usage anywhere:  showToast({ message: '…' })   or   showToast({ message, actionLabel, onAction })
 * <ToastHost /> is mounted once in App.
 */
export interface ToastOptions {
  message: string;
  tone?: 'info' | 'error';
  actionLabel?: string;
  onAction?: () => void;
  durationMs?: number; // default 5 s (errors 7 s)
}
interface ToastItem extends ToastOptions { id: number }

let nextId = 1;
let listener: ((t: ToastItem | null) => void) | null = null;
let current: ToastItem | null = null;
let timer: number | null = null;
let onExpire: (() => void) | null = null;

/** Show a message; replaces any message that is showing. Returns a function that hides it. */
export function showToast(opts: ToastOptions): () => void {
  if (timer) window.clearTimeout(timer);
  const item: ToastItem = { ...opts, id: nextId++ };
  current = item;
  listener?.(item);
  const ms = opts.durationMs ?? (opts.tone === 'error' ? 7000 : 5000);
  timer = window.setTimeout(() => hideToast(item.id), ms);
  return () => hideToast(item.id);
}

/** Called when a toast times out without its action being used (step 6: send the approval). */
export function setToastExpireHandler(fn: (() => void) | null) {
  onExpire = fn;
}

export function hideToast(id?: number) {
  if (id !== undefined && current?.id !== id) return;
  if (timer) window.clearTimeout(timer);
  timer = null;
  current = null;
  listener?.(null);
  const fn = onExpire;
  onExpire = null;
  fn?.();
}

export const ToastHost: React.FC = () => {
  const [toast, setToast] = useState<ToastItem | null>(current);
  useEffect(() => {
    listener = setToast;
    return () => {
      if (listener === setToast) listener = null;
    };
  }, []);

  return (
    <div
      aria-live="polite"
      role="status"
      className="fixed inset-x-0 z-[10070] flex justify-center px-4 pointer-events-none bottom-[calc(6rem+env(safe-area-inset-bottom,0px))] md:bottom-[calc(7rem+env(safe-area-inset-bottom,0px))]"
    >
      {toast && (
        <div
          key={toast.id}
          className={`pointer-events-auto w-full max-w-xl flex items-center gap-3 rounded-kid-card px-5 py-4 shadow-lg font-bold text-base md:text-lg ${
            toast.tone === 'error' ? 'bg-red-700 text-white' : 'bg-kid-toast text-kid-toast-text'
          }`}
        >
          <span className="flex-1">{toast.message}</span>
          {toast.actionLabel && toast.onAction && (
            <button
              type="button"
              onClick={() => {
                const action = toast.onAction;
                onExpire = null; // the action replaces the expiry behaviour
                hideToast(toast.id);
                action?.();
              }}
              className="shrink-0 min-h-[44px] px-4 rounded-full bg-kid-card text-kid-text font-kid-heading font-extrabold"
            >
              {toast.actionLabel}
            </button>
          )}
          <button
            type="button"
            aria-label="Close"
            onClick={() => hideToast(toast.id)}
            className="shrink-0 w-11 h-11 -mr-2 rounded-full text-xl leading-none opacity-80 hover:opacity-100"
          >
            ×
          </button>
        </div>
      )}
    </div>
  );
};
